import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import products from '../data/products';
import '../styles.css';

const BASE_PRICES = {
  pro: 149,
  classic: 119,
  lite: 99,
  baby: 119  // --- IGNORE ---
};

const STORAGE_PRICES = {
  '32': 0,
  '64': 19,
  '256': 29,
  '1024': 49,
};

const MEMORY_PRICES = {
  '1': 0,
  '4': 40,
  '8': 60,
};

export default function ProductPage() {
  const { id } = useParams();

  // Keep landing at the top when navigating or switching products
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [id]);

  const product = products.find((p) => p.id === id);
  if (!product) return <Navigate to="/products" replace />;

  const [storage, setStorage] = useState('32');
  const [memory, setMemory]   = useState('1');

  const basePrice = BASE_PRICES[id] ?? 0;
  const price = useMemo(
    () => basePrice + STORAGE_PRICES[storage] + MEMORY_PRICES[memory],
    [basePrice, storage, memory]
  );

  const vendor = product.externalUrl.includes('amazon') ? 'Amazon' : 'Shopify';

  const {
    description = [],
    about = [],
    technicalSpecs = [],
    additionalInformation = [],
    warrantyAndSupport = '',
    feedback = ''
  } = product;

  // Normalize description to an array
  const descriptionParas = Array.isArray(description)
    ? description
    : (description ? [description] : []);

  // Slideshow state
  const hasImages = Array.isArray(product.images) && product.images.length > 0;
  const [selectedIndex, setSelectedIndex] = useState(() => (hasImages ? 0 : -1));
  const detailSrc = hasImages ? product.images[selectedIndex] : product.icon;

  // Zoom/Lightbox
  const [isZoomOpen, setIsZoomOpen] = useState(false);
  const openZoom  = useCallback(() => setIsZoomOpen(true), []);
  const closeZoom = useCallback(() => setIsZoomOpen(false), []);
  const onDetailKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setIsZoomOpen(true);
    }
  }, []);

  // Helper: render a description block that might contain HTML
  const renderDesc = (para, i) => {
    if (typeof para === 'string') {
      const looksLikeHtml = /<\/?[a-z][\s\S]*>/i.test(para);
      if (looksLikeHtml) {
        return (
          <div
            key={i}
            className="product-page-desc"
            dangerouslySetInnerHTML={{ __html: para }}
          />
        );
      }
      return (
        <p key={i} className="product-page-desc">
          {para}
        </p>
      );
    }
    if (para && typeof para === 'object' && typeof para.html === 'string') {
      return (
        <div
          key={i}
          className="product-page-desc"
          dangerouslySetInnerHTML={{ __html: para.html }}
        />
      );
    }
    return (
      <div key={i} className="product-page-desc">
        {para}
      </div>
    );
  };

  return (
    <section className="product-page">
      <div
        className="product-page__title"
        dangerouslySetInnerHTML={{ __html: product.title }}
      />

      {/* Description with media floated on the right:
          - Main image on top
          - Thumbnails
          - Price + selectors directly below thumbnails
          - Text wraps on the left */}
      {descriptionParas.length > 0 && (
        <div className="product-page__description">
          <div className="product-page__media-float" aria-label="Product images and options">
            <img
              src={detailSrc}
              alt={`${product.title} detail`}
              className="product-page__detail-image--float"
              onClick={openZoom}
              tabIndex={0}
              onKeyDown={onDetailKeyDown}
            />

            {hasImages && (
              <div className="product-page__thumbs" role="listbox" aria-label="Image thumbnails">
                {product.images.map((src, i) => (
                  <button
                    key={`${src}-${i}`}
                    type="button"
                    className={`thumb ${i === selectedIndex ? 'active' : ''}`}
                    onClick={() => setSelectedIndex(i)}
                    aria-label={`View image ${i + 1}`}
                    title={`View image ${i + 1}`}
                  >
                    <img src={src} alt={`${product.title} thumbnail ${i + 1}`} loading="lazy" />
                  </button>
                ))}
              </div>
            )}

            {/* ✅ Price + selectors directly under the thumbnails in the right column */}
            <div className="product-page__options">
              <div className="product-page__price">${price.toFixed(2)}</div>
              <div className="product-page__selectors">
                <label>
                  Storage:
                  <select value={storage} onChange={(e) => setStorage(e.target.value)}>
                    <option value="32">32 GB</option>
                    <option value="64">64 GB</option>
                    <option value="256">256 GB</option>
                    <option value="1024">1 TB</option>
                  </select>
                </label>

                <label>
                  Memory:
                  <select value={memory} onChange={(e) => setMemory(e.target.value)}>
                    <option value="1">1 GB</option>
                    <option value="4">4 GB</option>
                    <option value="8">8 GB</option>
                  </select>
                </label>
              </div>
            </div>
          </div>

          {descriptionParas.map(renderDesc)}
        </div>
      )}

      {/* Lightbox */}
      {isZoomOpen && (
        <div
          className="lightbox"
          role="dialog"
          aria-label="Image zoom"
          onClick={closeZoom}
          onKeyDown={(e) => { if (e.key === 'Escape') closeZoom(); }}
          tabIndex={-1}
        >
          <img
            src={detailSrc}
            alt={`${product.title} zoomed`}
            className="lightbox__image"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            className="lightbox__close"
            aria-label="Close zoom"
            onClick={closeZoom}
            type="button"
          >
            ×
          </button>
        </div>
      )}

      {/* Technical Specifications (now ALSO contains About this item) */}
      {(technicalSpecs.length > 0 || about.length > 0) && (
        <div className="product-page__tech-specs">
          <h2>Technical Specifications</h2>

          {about.length > 0 && (
            <div className="product-page__about-in-tech">
              <h3>About this item</h3>
              <ul>
                {about.map((bullet, i) => (
                  <li key={i}>{bullet}</li>
                ))}
              </ul>
            </div>
          )}

          {technicalSpecs.length > 0 && (
            <table>
              <tbody>
                {technicalSpecs.map(({ label, value }, i) => (
                  <tr key={i}>
                    <th>{label}</th>
                    <td>{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {additionalInformation.length > 0 && (
        <div className="product-page__additional-info">
          <h2>Additional Information</h2>
          <table>
            <tbody>
              {additionalInformation.map(({ label, value }, i) => (
                <tr key={i}>
                  <th>{label}</th>
                  <td>{value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {warrantyAndSupport && (
        <div className="product-page__warranty-support">
          <h2>Warranty &amp; Support</h2>
          <p>{warrantyAndSupport}</p>
        </div>
      )}

      {feedback && (
        <div className="product-page__feedback">
          <h2>Feedback</h2>
          <p>{feedback}</p>
        </div>
      )}

      <a
        href={product.externalUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="product-page__buy-button"
      >
        Buy on {vendor}
      </a>

<style>{`
/*———————————————————————————————————
  Product Page
———————————————————————————————————*/

.product-page__title {
  font-size: 2rem;
  font-weight: 700;
}

/* Float container holding main image + thumbnails + options */
.product-page__media-float {
  float: right;
  width: min(42%, 340px);
  margin: 0 0 1rem 1rem;
  border-radius: 0.5rem;
}

/* Main image (in paragraph) */
.product-page__detail-image--float {
  display: block;
  width: 100%;
  height: auto;
  border-radius: 0.5rem;
  margin: 0 0 0.5rem 0;
  cursor: zoom-in;
  object-fit: cover;
}

/* Thumbnails (under main image, inside float) */
.product-page__thumbs {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-bottom: 0.5rem;
}
.product-page__thumbs .thumb {
  border: 1px solid var(--gray-border);
  background: transparent;
  padding: 0;
  cursor: pointer;
  border-radius: 0.375rem;
  overflow: hidden;
  line-height: 0;
  transition: transform 0.12s ease, box-shadow 0.12s ease, border-color 0.12s ease;
}
.product-page__thumbs .thumb:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0,0,0,0.08);
}
.product-page__thumbs .thumb.active {
  outline: 2px solid var(--blue-primary);
  border-color: var(--blue-primary);
}
.product-page__thumbs .thumb img {
  display: block;
  width: 64px;
  height: 64px;
  object-fit: cover;
}

/* ✅ Options block under thumbnails (right column) */
.product-page__options {
  border: 1px solid var(--gray-border);
  border-radius: 0.5rem;
  padding: 0.75rem;
  background: #fff;
}

.product-page__price {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
}

.product-page__selectors {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  margin: 0.25rem 0 0;
}
.product-page__selectors label {
  display: flex;
  flex-direction: column;
  font-weight: 600;
  font-size: 0.95rem;
}
.product-page__selectors select {
  margin-top: 0.35rem;
  padding: 0.45rem 0.5rem;
  border: 1px solid var(--gray-border);
  border-radius: 0.375rem;
}

/* Clear the float after the description block */
.product-page__description::after {
  content: "";
  display: table;
  clear: both;
}

/* Purple tech-specs box (now also contains About this item) */
.product-page__tech-specs {
  background: rgba(29,78,216,0.1);
  padding: 1.5rem;
  border-radius: 0.5rem;
  margin-bottom: 2rem;
}
.product-page__tech-specs h2 {
  margin-bottom: 1rem;
  font-size: 1.25rem;
  font-weight: 700;
}
.product-page__about-in-tech {
  margin-bottom: 1rem;
}
.product-page__about-in-tech h3 {
  margin: 0 0 0.5rem;
  font-size: 1.05rem;
  font-weight: 700;
}
.product-page__about-in-tech ul {
  list-style: disc;
  padding-left: 1.25rem;
  margin: 0;
}
.product-page__about-in-tech li {
  margin-bottom: 0.35rem;
}

/* Additional sections */
.product-page__additional-info table,
.product-page__tech-specs table {
  width: 100%;
  border-collapse: collapse;
}
.product-page__tech-specs th,
.product-page__tech-specs td,
.product-page__additional-info th,
.product-page__additional-info td {
  text-align: left;
  padding: 0.4rem 0.25rem;
  vertical-align: top;
  border-bottom: 1px solid rgba(0,0,0,0.06);
}

/* General page styles */
.product-page__description {
  margin-bottom: 0.5rem;
  font-size: 1rem;
  color: var(--text-dark);
}


.product-page {
  padding: 1rem 1.5rem;
  max-width: 800px;
  margin: 0 auto;
  box-sizing: border-box;
  text-align: left;
}
.product-page-desc p {
  font-size: 0.95em;
  line-height: 1.6;
  margin-bottom: 1rem;
}

/* Small screens: stack media above text */
@media (max-width: 640px) {
  .product-page__media-float {
    float: none;
    width: 100%;
    margin: 0 0 0.75rem 0;
  }
  .product-page__thumbs .thumb img {
    width: 56px;
    height: 56px;
  }
  .product-page__selectors {
    gap: 0.75rem;
  }
}
`}</style>
    </section>
  );
}
